"use client";

import React, { useState } from "react";
import { BadgeId, BadgeRarityType, Prediction } from "@/app/@types";
import { useAuth } from "@/hooks/useAuth";
import { useAppStore } from "@/store/useAppStore";
import { badgeSystem } from "@/utils/badgeSystem";
import { gameRules } from "@/utils/gameRules";
import { useAchievementToast } from "../AchievementToast";
import { FeedbackModal } from "./FeedbackModal";
import { updateUserScore } from "@/lib/user/updateUserScore";
import { createPrediction } from "@/lib/predictions/createPrediction";
import { awardBadge } from "@/lib/badges/awardBadge";
import { addPredictionActivity } from "@/lib/activity/addActivityToDB";
import { toast } from "react-toastify";

export const MarketPredictionModal = () => {
  const { user } = useAuth();
  const { showAchievement } = useAchievementToast();
  const {
    badges,
    selectedMarket,
    marketPrediction,
    completedMarkets,
    userScore,
    userEmail,
    predictions,
    showMarketModal,
    setShowMarketModal,
    setSelectedMarket,
    setMarketPrediction,
    updateUserPointsScore,
    setPredictions,
    setCompletedMarkets,
    setBadges,
    setNewBadge,
    setShowBadgeModal,
  } = useAppStore();

  const [showResultModal, setShowResultModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [resultData, setResultData] = useState<{
    points: number;
    feedback: string;
    prediction: number;
    crowdAverage: number;
    difference: number;
  } | null>(null);
  const [errorMessage, setErrorMessage] = useState("");

  const gameRulesObj = gameRules();
  const checkForNewBadges = () => {
    try {
      (Object.keys(badgeSystem) as BadgeId[]).forEach(async (badgeId) => {
        if (
          !badges.includes(badgeId) &&
          gameRulesObj.badges[badgeId]?.().earned
        ) {
          setBadges([...badges, badgeId]);
          user?.id && awardBadge(user.id, badgeId);
          setNewBadge(badgeSystem[badgeId]);
          setShowBadgeModal(true);

          const badgePoints = badgeSystem[badgeId].points;
          updateUserPointsScore(badgePoints);

          if (user?.id) {
            await updateUserScore(user.id, badgePoints);
          }
          await addPredictionActivity(badgePoints);

          const badge = badgeSystem[badgeId];
          showAchievement({
            id: badgeId,
            title: `${badge.name} Unlocked!`,
            description: badge.description,
            icon: badge.emoji,
            points: badge.points,
            rarity: badge.rarity.toLowerCase() as BadgeRarityType,
          });
        }
      });
    } catch (error) {
      toast.error("Error checking for new badges");
      console.error("Error checking badges:", error);
    }
  };

  const handleMarketPrediction = async () => {
    try {
      if (completedMarkets.includes(selectedMarket.id)) {
        setErrorMessage(
          "You have already made a prediction on this market!\n\nYou can only predict once per market to keep the game fair."
        );
        setShowErrorModal(true);
        return;
      }

      const prediction = parseInt(marketPrediction);
      if (isNaN(prediction) || prediction < 0 || prediction > 100) {
        setErrorMessage("Please enter a valid percentage between 0 and 100");
        setShowErrorModal(true);
        return;
      }

      const difference = Math.abs(prediction - selectedMarket.averageGuess);
      let points = 0;
      let feedback = "";

      if (difference <= 5) {
        points = 100;
        feedback = "🎯 Excellent! Very close to the crowd consensus!";
      } else if (difference <= 15) {
        points = 50;
        feedback = "👍 Good prediction! Reasonably aligned with the crowd.";
      } else {
        points = 25;
        feedback =
          "🤔 Interesting contrarian view! Time will tell if you're right.";
      }

      updateUserPointsScore(points);

      user?.id && (await updateUserScore(user.id, points));

      await addPredictionActivity(points);

      setCompletedMarkets([...completedMarkets, selectedMarket.id]);

      const newPrediction: Prediction = {
        id: Date.now().toString(),
        user_email: userEmail,
        question: selectedMarket.question,
        prediction: prediction,
        confidence: 50,
        reasoning: selectedMarket.inspiration,
        bias_check: "",
        created_at: new Date().toISOString(),
        status: "pending",
        points_earned: points,
      };
      user && (await createPrediction(user?.id, newPrediction));
      setPredictions([...predictions, newPrediction]);

      setResultData({
        points,
        feedback,
        prediction,
        crowdAverage: selectedMarket.averageGuess,
        difference,
      });
      setShowResultModal(true);

      setTimeout(() => checkForNewBadges(), 500);
    } catch (error) {
      console.log("Error handling market prediction: ", error);
      toast.error("Error handling market prediction");
    }
  };

  const handleResultModalClose = () => {
    setShowResultModal(false);
    setResultData(null);
    setShowMarketModal(false);
    setSelectedMarket(null);
    setMarketPrediction("");
  };

  const handleErrorModalClose = () => {
    setShowErrorModal(false);
    setErrorMessage("");
    if (completedMarkets.includes(selectedMarket.id)) {
      setShowMarketModal(false);
      setSelectedMarket(null);
      setMarketPrediction("");
    }
  };

  const getFeedbackStyle = () => {
    if (!resultData) return "info";

    if (resultData.difference <= 5) return "success";
    if (resultData.difference <= 15) return "brilliant";
    return "info";
  };

  if (!showMarketModal && !selectedMarket) {
    return null;
  }
  return (
    <>
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
        <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 border border-white/20 max-w-2xl w-full">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-2xl font-bold">📊 Make Your Prediction</h3>
            <button
              onClick={() => {
                setShowMarketModal(false);
                setSelectedMarket(null);
                setMarketPrediction("");
              }}
              className="text-gray-400 hover:text-white"
            >
              ✕
            </button>
          </div>

          <div className="mb-6">
            <h4 className="text-lg font-bold text-white mb-2">
              {selectedMarket.question}
            </h4>
            <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
              <div className="bg-white/5 rounded-xl p-3">
                <div className="text-gray-400">Time Left</div>
                <div className="font-bold text-white">
                  ⏰ {selectedMarket.timeLeft}
                </div>
              </div>
              <div className="bg-white/5 rounded-xl p-3">
                <div className="text-gray-400">Participants</div>
                <div className="font-bold text-white">
                  👥 {selectedMarket.participants.toLocaleString()}
                </div>
              </div>
              <div className="bg-white/5 rounded-xl p-3">
                <div className="text-gray-400">Crowd Average</div>
                <div className="font-bold text-white">
                  📊 {selectedMarket.averageGuess}%
                </div>
              </div>
              <div className="bg-white/5 rounded-xl p-3">
                <div className="text-gray-400">Prize</div>
                <div className="font-bold text-green-400">
                  {selectedMarket.prize}
                </div>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Your Prediction (0-100%)
              </label>
              <input
                type="number"
                value={marketPrediction}
                onChange={(e) => setMarketPrediction(e.target.value)}
                min="0"
                max="100"
                className="w-full p-4 bg-white/5 border border-white/10 rounded-xl text-white text-center text-2xl"
                placeholder="Enter percentage"
              />
              <div className="mt-2 text-xs text-gray-400 text-center">
                The crowd average is {selectedMarket.averageGuess}%. Will you
                follow or diverge?
              </div>
            </div>

            {selectedMarket.inspiration && (
              <div className="hidden sm:block mb-4 p-4 bg-blue-500/10 rounded-xl border border-blue-500/20">
                <p className="text-sm text-blue-300">
                  💡 {selectedMarket.inspiration}
                </p>
              </div>
            )}

            <button
              onClick={handleMarketPrediction}
              disabled={!marketPrediction || isNaN(Number(marketPrediction))}
              className="w-full bg-gradient-to-r from-brand-teal to-brand-amber text-white py-3 rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105 transition-all"
            >
              Submit Prediction
            </button>
          </div>
        </div>
      </div>
      {showResultModal && resultData && (
        <FeedbackModal
          type={getFeedbackStyle() as "success" | "error" | "info"}
          handleContinue={handleResultModalClose}
          showConfetti={resultData.difference <= 5}
        >
          <div className="text-6xl mb-4">
            {resultData.difference <= 5
              ? "🎯"
              : resultData.difference <= 15
              ? "👍"
              : "🤔"}
          </div>

          <h2 className="text-2xl font-bold text-white mb-4">
            Prediction Submitted!
          </h2>

          <div
            className={`text-xl font-semibold mb-6 ${
              resultData.points >= 75
                ? "text-green-400"
                : resultData.points >= 40
                ? "text-yellow-400"
                : "text-blue-400"
            }`}
          >
            +{resultData.points} points
          </div>

          <p className="text-gray-200 mb-6 leading-relaxed">
            {resultData.feedback}
          </p>

          <div className="bg-white/10 rounded-lg p-4 mb-6">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="text-center">
                <div className="text-brand-accent font-semibold">
                  Your Prediction
                </div>
                <div className="text-2xl font-bold text-white">
                  {resultData.prediction}%
                </div>
              </div>
              <div className="text-center">
                <div className="text-blue-300 font-semibold">Crowd Average</div>
                <div className="text-2xl font-bold text-white">
                  {resultData.crowdAverage}%
                </div>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-white/20 text-center">
              <div className="text-gray-300 text-sm">
                Difference:{" "}
                <span className="font-semibold">
                  {resultData.difference.toFixed(1)}%
                </span>
              </div>
            </div>
          </div>

          {resultData.difference <= 5 && (
            <div className="bg-green-500/20 rounded-lg p-3 mb-4 border border-green-500/30">
              <div className="text-green-300 text-sm font-semibold">
                🏆 Excellent prediction! You're in sync with the wisdom of
                crowds!
              </div>
            </div>
          )}

          <div className="text-sm text-gray-400">
            📈 Your prediction has been recorded and will be scored when the
            market resolves.
          </div>
        </FeedbackModal>
      )}

      {showErrorModal && (
        <FeedbackModal
          type="error"
          handleContinue={handleErrorModalClose}
          showConfetti={false}
        >
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-white mb-4">
            {completedMarkets.includes(selectedMarket.id)
              ? "Already Predicted"
              : "Invalid Input"}
          </h2>
          <p className="text-gray-200 mb-6 leading-relaxed whitespace-pre-line">
            {errorMessage}
          </p>
          {completedMarkets.includes(selectedMarket.id) && (
            <div className="bg-yellow-500/20 rounded-lg p-3 mb-4 border border-yellow-500/30">
              <div className="text-yellow-300 text-sm">
                🔒 This ensures fair play - each user gets one prediction per
                market.
              </div>
            </div>
          )}
        </FeedbackModal>
      )}
    </>
  );
};
